import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { HiOutlineX } from "react-icons/hi";

import DateTimePicker from "../../components/DateTimePicker";
import FilePreviewModal, {
  detectPreviewKind,
  type FilePreviewKind,
} from "components/FilePreviewModal";
import uploadDropzoneGraphic from "assets/svg/upload-modal-dropzone-graphic.svg";

import {
  getLegalCertificateFileForms,
  saveLegalCertificateFileForms,
  type LegalCertificateFileFormsApplicationItem,
} from "../../api/legalCertificate";
import { generateUploadPresignedUrls } from "../../api/discussion";
import { parseFilingApiDateString, toLocalYmd } from "../../utils/filingApiDates";
import { downloadFileFromUrlWithFallback } from "../../utils/downloadFileFromUrl";

export const LEGAL_CERTIFICATE_FILE_FORMS_FORM_ID =
  "legal-certificate-file-forms-form";

const ACCEPTED_TYPES = ".pdf";
const ACCEPTED_HELP = "PDF format, up to 50MB";
const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024;
const UPLOAD_FOLDER_PREFIX = "legal-certificate/filing-forms";

const LABEL_FEATURES = { fontFeatureSettings: "'ss01' on, 'cv01' on" } as const;

type ApplicationRowState = LegalCertificateFileFormsApplicationItem & {
  pendingFilename?: string | null;
};

type PreviewState = {
  url: string;
  name: string;
  kind: FilePreviewKind;
};

type LegalCertificateFileFormsViewProps = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
};

function dateValue(value: string | null | undefined): Date | null {
  return parseFilingApiDateString(value ?? null);
}

function toIsoDate(value: string | null | undefined): string | undefined {
  const d = parseFilingApiDateString(value ?? null);
  return d ? toLocalYmd(d) : undefined;
}

type FieldErrors = {
  noOfRegistrationCertificate?: boolean;
  dateOfRegistrationCertificate?: boolean;
  dateOfFilingLegalCertificate?: boolean;
};

function parseRegistrationNumberInput(value: string): number | null {
  const digitsOnly = value.replace(/\D/g, "");
  if (!digitsOnly) return null;
  const parsed = Number.parseInt(digitsOnly, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

export default function LegalCertificateFileFormsView({
  leadId,
  onSuccess,
}: LegalCertificateFileFormsViewProps) {
  const [applications, setApplications] = useState<ApplicationRowState[]>([]);
  const [loading, setLoading] = useState<boolean>(!!leadId);
  const [saving, setSaving] = useState(false);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [uploadingIds, setUploadingIds] = useState<Record<string, boolean>>({});
  const [preview, setPreview] = useState<PreviewState | null>(null);
  const [errors, setErrors] = useState<Record<string, FieldErrors>>({});

  const autoSaveTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>(
    {}
  );

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      setApplications([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getLegalCertificateFileForms(leadId)
      .then((data) => {
        if (cancelled) return;
        setApplications(data.applications ?? []);
      })
      .catch((err) => {
        if (cancelled) return;
        toast.error(
          err instanceof Error
            ? err.message
            : "Failed to load File Forms data"
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  useEffect(() => {
    return () => {
      Object.values(autoSaveTimers.current).forEach((t) => clearTimeout(t));
      autoSaveTimers.current = {};
    };
  }, []);

  const clearFieldError = (applicationId: string, field: keyof FieldErrors) => {
    setErrors((prev) => {
      const existing = prev[applicationId];
      if (!existing?.[field]) return prev;
      const nextRow = { ...existing };
      delete nextRow[field];
      const next = { ...prev };
      if (Object.keys(nextRow).length === 0) {
        delete next[applicationId];
      } else {
        next[applicationId] = nextRow;
      }
      return next;
    });
  };

  const scheduleAutoSave = (
    applicationId: string,
    patch: {
      noOfRegistrationCertificate?: number | null;
      dateOfRegistrationCertificate?: string | null;
      dateOfFilingLegalCertificate?: string | null;
    },
    keySuffix: string
  ) => {
    if (!leadId) return;
    const key = `${keySuffix}-${applicationId}`;
    const existing = autoSaveTimers.current[key];
    if (existing) clearTimeout(existing);
    autoSaveTimers.current[key] = setTimeout(() => {
      saveLegalCertificateFileForms(leadId, {
        applications: [{ applicationId, ...patch }],
      })
        .then((data) => {
          setApplications((prev) =>
            mergeApplications(prev, data.applications)
          );
        })
        .catch(() => {
          // Auto-save errors are non-blocking; final submit will surface real issues.
        });
    }, 600);
  };

  const handleRegistrationNumberChange = (
    applicationId: string,
    raw: string
  ) => {
    const next = parseRegistrationNumberInput(raw);
    setApplications((prev) =>
      prev.map((app) =>
        app.applicationId === applicationId
          ? { ...app, noOfRegistrationCertificate: next }
          : app
      )
    );
    scheduleAutoSave(
      applicationId,
      { noOfRegistrationCertificate: next },
      "regNo"
    );
    if (next != null) clearFieldError(applicationId, "noOfRegistrationCertificate");
  };

  const handleRegistrationDateChange = (
    applicationId: string,
    next: Date | null
  ) => {
    const iso = next ? toLocalYmd(next) : null;
    setApplications((prev) =>
      prev.map((app) =>
        app.applicationId === applicationId
          ? { ...app, dateOfRegistrationCertificate: iso }
          : app
      )
    );
    scheduleAutoSave(
      applicationId,
      { dateOfRegistrationCertificate: iso },
      "regDate"
    );
    if (iso) clearFieldError(applicationId, "dateOfRegistrationCertificate");
  };

  const handleFilingDateChange = (
    applicationId: string,
    next: Date | null
  ) => {
    const iso = next ? toLocalYmd(next) : null;
    setApplications((prev) =>
      prev.map((app) =>
        app.applicationId === applicationId
          ? { ...app, dateOfFilingLegalCertificate: iso }
          : app
      )
    );
    scheduleAutoSave(
      applicationId,
      { dateOfFilingLegalCertificate: iso },
      "filingDate"
    );
    if (iso) clearFieldError(applicationId, "dateOfFilingLegalCertificate");
  };

  const setRowUploading = (applicationId: string, value: boolean) => {
    setUploadingIds((prev) => {
      if (value) return { ...prev, [applicationId]: true };
      const next = { ...prev };
      delete next[applicationId];
      return next;
    });
  };

  const uploadFileForRow = async (applicationId: string, file: File) => {
    if (!leadId) return;
    if (file.size > MAX_FILE_SIZE_BYTES) {
      toast.error(`${file.name} exceeds the 50MB limit`);
      return;
    }
    setApplications((prev) =>
      prev.map((app) =>
        app.applicationId === applicationId
          ? { ...app, pendingFilename: file.name }
          : app
      )
    );
    setRowUploading(applicationId, true);
    try {
      const [presigned] = await generateUploadPresignedUrls(
        [
          {
            filename: file.name,
            mimeType: file.type || "application/pdf",
          },
        ],
        "private",
        `${UPLOAD_FOLDER_PREFIX}/${leadId}`
      );
      if (!presigned?.url || !presigned?.key) {
        throw new Error("Failed to generate upload URL");
      }

      const putRes = await fetch(presigned.url, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type || "application/pdf" },
      });
      if (!putRes.ok) {
        throw new Error(`Failed to upload ${file.name}`);
      }

      const data = await saveLegalCertificateFileForms(leadId, {
        applications: [
          {
            applicationId,
            tmMFormPdfFileKey: presigned.key,
            tmMFormPdfFilename: file.name,
          },
        ],
      });

      setApplications((prev) => mergeApplications(prev, data.applications));
      toast.success(`Uploaded ${file.name}`);
    } catch (err) {
      setApplications((prev) =>
        prev.map((app) =>
          app.applicationId === applicationId
            ? { ...app, pendingFilename: null }
            : app
        )
      );
      toast.error(err instanceof Error ? err.message : "Failed to upload file");
    } finally {
      setRowUploading(applicationId, false);
    }
  };

  const removeUploadedPdf = async (applicationId: string) => {
    if (!leadId) return;
    try {
      const data = await saveLegalCertificateFileForms(leadId, {
        applications: [
          {
            applicationId,
            tmMFormPdfFileKey: null,
            tmMFormPdfFilename: null,
          },
        ],
      });
      setApplications((prev) => mergeApplications(prev, data.applications));
      toast.success("File removed.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to remove file");
    }
  };

  const openPreview = (row: ApplicationRowState) => {
    if (!row.pdfDownloadUrl || !row.tmMFormPdfFilename) {
      toast.error("Preview is not available yet.");
      return;
    }
    setPreview({
      url: row.pdfDownloadUrl,
      name: row.tmMFormPdfFilename,
      kind: detectPreviewKind(row.tmMFormPdfFilename),
    });
  };

  const closePreview = () => setPreview(null);

  const handleDownload = async (row: ApplicationRowState) => {
    if (!row.pdfDownloadUrl || !row.tmMFormPdfFilename) {
      toast.error("No file available to download.");
      return;
    }
    try {
      const result = await downloadFileFromUrlWithFallback(
        row.pdfDownloadUrl,
        row.tmMFormPdfFilename
      );
      if (result === "local") {
        toast.success("File saved to your device.");
      } else {
        toast.info(
          "Opened the file in a new tab. Use Save or Save as to store it locally."
        );
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to download file");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || saving) return;
    if (applications.length === 0) {
      toast.error("No applications found. Complete the previous steps first.");
      return;
    }
    const nextErrors: Record<string, FieldErrors> = {};
    applications.forEach((app) => {
      const rowErrors: FieldErrors = {};
      if (app.noOfRegistrationCertificate == null) {
        rowErrors.noOfRegistrationCertificate = true;
      }
      if (!toIsoDate(app.dateOfRegistrationCertificate)) {
        rowErrors.dateOfRegistrationCertificate = true;
      }
      if (!toIsoDate(app.dateOfFilingLegalCertificate)) {
        rowErrors.dateOfFilingLegalCertificate = true;
      }
      if (Object.keys(rowErrors).length > 0) {
        nextErrors[app.applicationId] = rowErrors;
      }
    });
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      toast.error("Please fill in all required fields.");
      return;
    }
    const hasAnyUpload = applications.some(
      (app) => !!app.tmMFormPdfFileKey || !!app.tmMFormPdfFilename
    );
    if (!hasAnyUpload) {
      toast.error(
        "Please upload the TM-M Form PDF for at least one application to continue."
      );
      return;
    }
    setErrors({});
    Object.values(autoSaveTimers.current).forEach((t) => clearTimeout(t));
    autoSaveTimers.current = {};
    setSaving(true);
    try {
      const data = await saveLegalCertificateFileForms(leadId, {
        applications: applications.map((app) => ({
          applicationId: app.applicationId,
          noOfRegistrationCertificate: app.noOfRegistrationCertificate ?? null,
          dateOfRegistrationCertificate:
            toIsoDate(app.dateOfRegistrationCertificate) ?? null,
          dateOfFilingLegalCertificate:
            toIsoDate(app.dateOfFilingLegalCertificate) ?? null,
        })),
      });
      setApplications((prev) => mergeApplications(prev, data.applications));
      toast.success("File Forms for Legal Certificate saved successfully.");
      onSuccess?.(leadId);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (!leadId) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          Please complete the previous steps (select or create a lead) to access
          this form.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">Loading File Forms data...</p>
      </div>
    );
  }

  if (applications.length === 0) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          No applications found. Please complete the Application Details step
          first.
        </p>
      </div>
    );
  }

  return (
    <form
      id={LEGAL_CERTIFICATE_FILE_FORMS_FORM_ID}
      onSubmit={handleSubmit}
      className="flex flex-col gap-4 md:gap-5 w-full"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {/* Desktop / tablet: table layout */}
      <div className="hidden md:block w-full overflow-x-auto">
        <table className="w-full border-separate border-spacing-y-2 min-w-[920px]">
          <thead>
            <tr>
              <th
                className="text-left text-xs font-normal text-[#222] px-1 pb-1"
                style={LABEL_FEATURES}
              >
                TM Number
              </th>
              <th
                className="text-left text-xs font-normal text-[#222] px-1 pb-1"
                style={LABEL_FEATURES}
              >
                No. of Reg Certificate
              </th>
              <th
                className="text-left text-xs font-normal text-[#222] px-1 pb-1"
                style={LABEL_FEATURES}
              >
                Date of Reg Certificate
              </th>
              <th
                className="text-left text-xs font-normal text-[#222] px-1 pb-1"
                style={LABEL_FEATURES}
              >
                Date of Filing
              </th>
              <th
                className="text-left text-xs font-normal text-[#222] px-1 pb-1"
                style={LABEL_FEATURES}
              >
                Upload File
              </th>
            </tr>
          </thead>
          <tbody>
            {applications.map((row) => {
              const inputId = `lc-file-forms-input-${row.applicationId}`;
              const uploading = !!uploadingIds[row.applicationId];
              const dragging = draggingId === row.applicationId;
              const fileLabel =
                row.pendingFilename ?? row.tmMFormPdfFilename ?? null;
              const hasFile = !!fileLabel;
              const rowErrors = errors[row.applicationId] ?? {};

              return (
                <tr key={row.applicationId} className="align-top">
                  <td className="px-1">
                    <div
                      className="box-border flex flex-row items-center gap-2.5 px-[10px] py-2 h-9 w-full bg-[#F7F7F7] border border-[#EEEEEE] rounded-[6px] text-[12px] font-normal leading-5 text-[#222222] truncate"
                      style={LABEL_FEATURES}
                      title={row.tmNo ?? ""}
                    >
                      <span className="truncate">{row.tmNo || "—"}</span>
                    </div>
                  </td>

                  <td className="px-1">
                    <input
                      type="text"
                      inputMode="numeric"
                      autoComplete="off"
                      value={
                        row.noOfRegistrationCertificate != null
                          ? String(row.noOfRegistrationCertificate)
                          : ""
                      }
                      onChange={(e) =>
                        handleRegistrationNumberChange(
                          row.applicationId,
                          e.target.value
                        )
                      }
                      placeholder="e.g., 1234567"
                      className={`box-border w-full h-9 px-[10px] py-2 bg-white border rounded-[6px] text-[12px] leading-5 text-[#222222] focus:outline-none focus:ring-1 ${
                        rowErrors.noOfRegistrationCertificate
                          ? "border-red-400 ring-1 ring-red-400 focus:ring-red-400 focus:border-red-400"
                          : "border-[#D5D3E0] focus:ring-[#222] focus:border-[#222]"
                      }`}
                      style={LABEL_FEATURES}
                    />
                  </td>

                  <td className="px-1">
                    <DateTimePicker
                      value={dateValue(row.dateOfRegistrationCertificate)}
                      onChange={(d) =>
                        handleRegistrationDateChange(row.applicationId, d)
                      }
                      showTime={false}
                      buttonClassName={`rounded-[6px] text-[12px] ${
                        rowErrors.dateOfRegistrationCertificate
                          ? "!border-red-400 !ring-1 !ring-red-400"
                          : ""
                      }`}
                    />
                  </td>

                  <td className="px-1">
                    <DateTimePicker
                      value={dateValue(row.dateOfFilingLegalCertificate)}
                      onChange={(d) =>
                        handleFilingDateChange(row.applicationId, d)
                      }
                      showTime={false}
                      buttonClassName={`rounded-[6px] text-[12px] ${
                        rowErrors.dateOfFilingLegalCertificate
                          ? "!border-red-400 !ring-1 !ring-red-400"
                          : ""
                      }`}
                    />
                  </td>

                  <td className="pl-1">
                    <div className="flex flex-col gap-2">
                      <div
                        className={`flex flex-row justify-between items-center gap-3 px-3 py-2 rounded-[8px] border border-dashed bg-white transition-colors cursor-pointer ${
                          dragging
                            ? "border-[#222] bg-[#F5F5F5]"
                            : hasFile
                              ? "border-[#22C55E] bg-[#F0FDF4]"
                              : "border-[#D8DAEB]"
                        }`}
                        onDragOver={(e) => {
                          e.preventDefault();
                          setDraggingId(row.applicationId);
                        }}
                        onDragLeave={() => setDraggingId(null)}
                        onDrop={(e) => {
                          e.preventDefault();
                          setDraggingId(null);
                          if (uploading) return;
                          const file = e.dataTransfer.files?.[0];
                          if (file)
                            void uploadFileForRow(row.applicationId, file);
                        }}
                        onClick={() => {
                          if (uploading) return;
                          document.getElementById(inputId)?.click();
                        }}
                      >
                        <div className="flex flex-row items-center gap-3 min-w-0">
                          <img
                            src={uploadDropzoneGraphic}
                            alt=""
                            width={32}
                            height={32}
                            className="flex-shrink-0"
                          />
                          <div className="flex flex-col gap-0.5 min-w-0">
                            {hasFile ? (
                              <>
                                <p
                                  className="font-medium text-[#16A34A] truncate text-[12px] leading-4"
                                  title={fileLabel ?? ""}
                                >
                                  {fileLabel}
                                </p>
                                <p className="text-[10px] leading-3 text-[#7D7D7D]">
                                  {uploading ? "Uploading…" : ACCEPTED_HELP}
                                </p>
                              </>
                            ) : (
                              <>
                                <p className="font-medium text-[#101111] text-[12px] leading-4">
                                  Drag and drop your files
                                </p>
                                <p className="text-[10px] leading-3 text-[#7D7D7D]">
                                  {uploading ? "Uploading…" : ACCEPTED_HELP}
                                </p>
                              </>
                            )}
                          </div>
                        </div>
                        <button
                          type="button"
                          disabled={uploading}
                          onClick={(e) => {
                            e.stopPropagation();
                            document.getElementById(inputId)?.click();
                          }}
                          className="flex-shrink-0 flex items-center justify-center h-8 px-3 rounded-[8px] bg-white border border-[#D5D3E0] text-[#2E3037] hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed text-[12px] font-medium"
                          style={{
                            boxShadow: "0px 5px 20px rgba(0, 0, 0, 0.02)",
                          }}
                        >
                          {hasFile ? "Replace" : "Select File"}
                        </button>
                      </div>

                      <input
                        id={inputId}
                        type="file"
                        accept={ACCEPTED_TYPES}
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file)
                            void uploadFileForRow(row.applicationId, file);
                          e.currentTarget.value = "";
                        }}
                      />

                      {row.tmMFormPdfFilename && row.pdfDownloadUrl && (
                        <div className="flex items-center justify-end gap-2 flex-wrap">
                          <button
                            type="button"
                            onClick={() => openPreview(row)}
                            className="px-2.5 h-7 rounded-[6px] border border-[#D5D3E0] bg-white text-[11px] font-medium text-[#2E3037] hover:bg-gray-50"
                          >
                            Preview
                          </button>
                          <button
                            type="button"
                            onClick={() => void handleDownload(row)}
                            className="px-2.5 h-7 rounded-[6px] border border-[#D5D3E0] bg-white text-[11px] font-medium text-[#2E3037] hover:bg-gray-50"
                          >
                            Download
                          </button>
                          <button
                            type="button"
                            aria-label={`Remove ${row.tmMFormPdfFilename}`}
                            onClick={() =>
                              void removeUploadedPdf(row.applicationId)
                            }
                            className="text-[#6D6D6D] hover:text-[#222] p-1"
                            title="Remove"
                          >
                            <HiOutlineX className="w-3.5 h-3.5" strokeWidth={2} />
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile: card per application */}
      <div className="md:hidden flex flex-col gap-4">
        {applications.map((row) => {
          const inputId = `lc-file-forms-input-mobile-${row.applicationId}`;
          const uploading = !!uploadingIds[row.applicationId];
          const dragging = draggingId === `m-${row.applicationId}`;
          const fileLabel =
            row.pendingFilename ?? row.tmMFormPdfFilename ?? null;
          const hasFile = !!fileLabel;
          const rowErrors = errors[row.applicationId] ?? {};

          return (
            <div
              key={row.applicationId}
              className="flex flex-col gap-3 p-4 rounded-[10px] border border-[#EEE] bg-white"
            >
              <div className="flex items-center gap-2 text-xs font-semibold text-[#222]">
                <span>TM No.</span>
                <span className="text-[#101828]">{row.tmNo || "—"}</span>
              </div>

              <div className="flex flex-col gap-1.5">
                <label
                  className="text-xs font-normal text-[#222]"
                  style={LABEL_FEATURES}
                >
                  No. of Reg Certificate
                  <span className="text-[#E7000B]"> *</span>
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  value={
                    row.noOfRegistrationCertificate != null
                      ? String(row.noOfRegistrationCertificate)
                      : ""
                  }
                  onChange={(e) =>
                    handleRegistrationNumberChange(
                      row.applicationId,
                      e.target.value
                    )
                  }
                  placeholder="e.g., 1234567"
                  className={`box-border w-full min-h-[44px] px-[10px] py-2 bg-white border rounded-[6px] text-[12px] leading-5 text-[#222222] focus:outline-none focus:ring-1 ${
                    rowErrors.noOfRegistrationCertificate
                      ? "border-red-400 ring-1 ring-red-400 focus:ring-red-400 focus:border-red-400"
                      : "border-[#D5D3E0] focus:ring-[#222] focus:border-[#222]"
                  }`}
                  style={LABEL_FEATURES}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label
                  className="text-xs font-normal text-[#222]"
                  style={LABEL_FEATURES}
                >
                  Date of Reg Certificate
                  <span className="text-[#E7000B]"> *</span>
                </label>
                <DateTimePicker
                  value={dateValue(row.dateOfRegistrationCertificate)}
                  onChange={(d) =>
                    handleRegistrationDateChange(row.applicationId, d)
                  }
                  showTime={false}
                  buttonClassName={`min-h-[44px] rounded-[6px] text-[12px] ${
                    rowErrors.dateOfRegistrationCertificate
                      ? "!border-red-400 !ring-1 !ring-red-400"
                      : ""
                  }`}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label
                  className="text-xs font-normal text-[#222]"
                  style={LABEL_FEATURES}
                >
                  Date of Filing
                  <span className="text-[#E7000B]"> *</span>
                </label>
                <DateTimePicker
                  value={dateValue(row.dateOfFilingLegalCertificate)}
                  onChange={(d) =>
                    handleFilingDateChange(row.applicationId, d)
                  }
                  showTime={false}
                  buttonClassName={`min-h-[44px] rounded-[6px] text-[12px] ${
                    rowErrors.dateOfFilingLegalCertificate
                      ? "!border-red-400 !ring-1 !ring-red-400"
                      : ""
                  }`}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label
                  className="text-xs font-normal text-[#222]"
                  style={LABEL_FEATURES}
                >
                  Upload File
                </label>
                <div
                  className={`flex flex-row items-center justify-between gap-3 px-3 py-3 rounded-[10px] border border-dashed bg-white transition-colors cursor-pointer ${
                    dragging
                      ? "border-[#222] bg-[#F5F5F5]"
                      : hasFile
                        ? "border-[#22C55E] bg-[#F0FDF4]"
                        : "border-[#D8DAEB]"
                  }`}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDraggingId(`m-${row.applicationId}`);
                  }}
                  onDragLeave={() => setDraggingId(null)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDraggingId(null);
                    if (uploading) return;
                    const file = e.dataTransfer.files?.[0];
                    if (file) void uploadFileForRow(row.applicationId, file);
                  }}
                  onClick={() => {
                    if (uploading) return;
                    document.getElementById(inputId)?.click();
                  }}
                >
                  <div className="flex flex-row items-center gap-3 min-w-0">
                    <img
                      src={uploadDropzoneGraphic}
                      alt=""
                      width={40}
                      height={40}
                      className="flex-shrink-0"
                    />
                    <div className="flex flex-col gap-0.5 min-w-0">
                      {hasFile ? (
                        <>
                          <p
                            className="font-medium text-[#16A34A] truncate text-[13px] leading-5"
                            title={fileLabel ?? ""}
                          >
                            {fileLabel}
                          </p>
                          <p className="text-[11px] text-[#7D7D7D]">
                            {uploading ? "Uploading…" : ACCEPTED_HELP}
                          </p>
                        </>
                      ) : (
                        <>
                          <p className="font-medium text-[#101111] text-[13px] leading-5">
                            Drag and drop your files
                          </p>
                          <p className="text-[11px] text-[#7D7D7D]">
                            {uploading ? "Uploading…" : ACCEPTED_HELP}
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    disabled={uploading}
                    onClick={(e) => {
                      e.stopPropagation();
                      document.getElementById(inputId)?.click();
                    }}
                    className="flex-shrink-0 flex items-center justify-center h-9 px-3 rounded-[8px] bg-white border border-[#D5D3E0] text-[#2E3037] hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed text-[12px] font-medium"
                  >
                    {hasFile ? "Replace" : "Select File"}
                  </button>
                </div>

                <input
                  id={inputId}
                  type="file"
                  accept={ACCEPTED_TYPES}
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) void uploadFileForRow(row.applicationId, file);
                    e.currentTarget.value = "";
                  }}
                />

                {row.tmMFormPdfFilename && row.pdfDownloadUrl && (
                  <div className="flex items-center justify-end gap-2 flex-wrap mt-1">
                    <button
                      type="button"
                      onClick={() => openPreview(row)}
                      className="px-3 h-8 rounded-[8px] border border-[#D5D3E0] bg-white text-[12px] font-medium text-[#2E3037] hover:bg-gray-50"
                    >
                      Preview
                    </button>
                    <button
                      type="button"
                      onClick={() => void handleDownload(row)}
                      className="px-3 h-8 rounded-[8px] border border-[#D5D3E0] bg-white text-[12px] font-medium text-[#2E3037] hover:bg-gray-50"
                    >
                      Download
                    </button>
                    <button
                      type="button"
                      aria-label={`Remove ${row.tmMFormPdfFilename}`}
                      onClick={() => void removeUploadedPdf(row.applicationId)}
                      className="text-[#6D6D6D] hover:text-[#222] p-1"
                      title="Remove"
                    >
                      <HiOutlineX className="w-4 h-4" strokeWidth={2} />
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <FilePreviewModal
        open={!!preview}
        onClose={closePreview}
        name={preview?.name ?? ""}
        url={preview?.url}
        kind={preview?.kind}
      />
    </form>
  );
}

function mergeApplications(
  prev: ApplicationRowState[],
  next: LegalCertificateFileFormsApplicationItem[]
): ApplicationRowState[] {
  const prevById = new Map(prev.map((a) => [a.applicationId, a]));
  return next.map((app) => {
    const cached = prevById.get(app.applicationId);
    return {
      ...app,
      pendingFilename: cached?.pendingFilename ?? null,
    };
  });
}
